import { z } from 'zod';

import {
  indexSearchSchema,
  listParams,
  type FilterField,
  type ListParams,
  type SearchParamValue,
} from '@nucleus-commerce/admin-kit/index-table';

import {
  ATTRIBUTE_FILTER_PREFIX,
  productFilterFields,
  type ProductScopeKind,
} from './product-filters';

/** The URL param names of every product filter, in either scope (`productFilterFields`). */
type ProductFilterParam =
  | 'status'
  | 'status_not'
  | 'productTypeId'
  | 'collectionId'
  | 'collectionId_none'
  | 'tags'
  | 'tagsAll'
  | 'vendor'
  | 'price_gte'
  | 'price_lte'
  | 'stock'
  | 'inStoreId'
  | 'notInStoreId'
  | 'inAnyStore'
  | 'needsAttention'
  | 'createdAt_gte'
  | 'createdAt_lte'
  | 'updatedAt_gte'
  | 'updatedAt_lte'
  | 'publishedAt_gte'
  | 'publishedAt_lte'
  | 'hasImage'
  | 'scheduled'
  | 'scheduleState'
  | 'scheduleId'
  | 'fulfillmentMode'
  | 'unpriced';

type Param = SearchParamValue | undefined;

/**
 * A product list route's validated search. Spelled out rather than inferred so it has no plain
 * string index signature, which would widen every other route's params in `useSearch({ strict:
 * false })`.
 */
export type ProductListSearch<S extends string, X extends string = never> = {
  cursor?: string | undefined;
  limit?: number | undefined;
  q?: string | undefined;
  sort?: S | `-${S}` | undefined;
} & Partial<Record<ProductFilterParam | X, Param>> &
  Partial<Record<`attr.${string}`, Param>>;

export interface ProductListSearchOptions<S extends string, X extends string = never> {
  /** Fields the endpoint sorts on. */
  sort: readonly S[];
  /** Filters the screen adds to the shared ones (the store catalog's one master product). */
  extraFields?: ReadonlyArray<FilterField & { key: X }>;
  /** Old param name -> new one, read when the new one is absent (links made before D-182). */
  legacy?: Readonly<Record<string, string>>;
}

const attrValue = z
  .union([z.string().trim().min(1), z.number(), z.boolean()])
  .optional()
  .catch(undefined);

/**
 * The `validateSearch` of a product list screen (Products, Store catalog): cursor, limit, q,
 * sort and every filter of the scope (`productFilterFields`) under the list endpoint's names, so
 * the screen, the product picker and a selection built from the URL share them. `attr.<key>`
 * params are kept as they come (their keys are only known once the dictionary loads); other
 * unknown params are dropped. Legacy params are read into their new names.
 */
export function productListSearchSchema<const S extends string, const X extends string = never>(
  scope: ProductScopeKind,
  { sort, extraFields = [], legacy = {} }: ProductListSearchOptions<S, X>,
): z.ZodType<ProductListSearch<S, X>> {
  const base = indexSearchSchema([...productFilterFields(scope), ...extraFields], { sort });
  const known = new Set(Object.keys(base.shape));
  const schema = z.preprocess((raw) => {
    if (typeof raw !== 'object' || raw === null) return raw;
    const input = raw as Record<string, unknown>;
    const search: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      if (key in legacy) continue;
      if (known.has(key) || key.startsWith(ATTRIBUTE_FILTER_PREFIX)) search[key] = value;
    }
    for (const [from, to] of Object.entries(legacy)) {
      if (input[from] !== undefined && search[to] === undefined) search[to] = input[from];
    }
    return search;
  }, base.catchall(attrValue));
  return schema as unknown as z.ZodType<ProductListSearch<S, X>>;
}

/**
 * The list request for a validated search, as `useSearchListState().params` builds it from the
 * screen's filter defs, plus `includeTotal` on the first page: the loader's prefetch and the
 * screen's query then share one key, and the total is asked once per query, not per page.
 */
export function productListSearchParams(
  search: Readonly<Record<string, unknown>>,
  scope: ProductScopeKind,
  extraFields: readonly FilterField[] = [],
): ListParams {
  return withFirstPageTotal(
    listParams(search, [...productFilterFields(scope, search), ...extraFields]),
  );
}

/** `includeTotal` on a first page (no cursor); later pages keep the first page's total. */
export function withFirstPageTotal(params: ListParams): ListParams {
  return params.cursor === undefined ? { ...params, includeTotal: 'true' } : params;
}
